/**
 * DYNAMIC COMMISSION ENGINE -- PHASE 1 (pure logic, NOT yet connected to the app).
 *
 * Nothing in the live app calls this file yet, so agents' commission figures do
 * not change until a later batch wires it in and you approve that switch.
 *
 * Rules, as agreed in the master specification (11 Oct 2026 addendum):
 *   - Every condition in a rule is checked. Empty optional conditions do not
 *     restrict the rule.
 *   - Two or more matching rules for one recovery entry = CONFLICT. No rule wins
 *     by priority, level, specificity, newest, or highest/lowest rate.
 *   - A matching rule with missing or invalid data = BLOCKED. Nothing is guessed
 *     and no other amount is substituted.
 *   - No matching rule = NO_MATCH. No default rate is applied.
 *   - Account age runs from the imported NPA date to the entry's collection date.
 *     From is inclusive, To is exclusive. Months and years are calendar-based.
 *   - An age band that matches overrides the rule's general rate. Rates are never added.
 *   - Effective dates and additional date conditions are checked on the collection
 *     date only. Both ends are inclusive.
 *   - Commission = base x rate / 100, calculated exactly in paise (1/100 rupee),
 *     rounded half-up to 2 decimal places (paise). Rates may have up to 4 decimals.
 */

export type AmountBasis = 'collection' | 'outstanding' | 'principal' | 'totalRecoverable';
export type AgeUnit = 'days' | 'months' | 'years';

export const AMOUNT_BASES: AmountBasis[] = ['collection', 'outstanding', 'principal', 'totalRecoverable'];

export interface AgeBand {
  id: string;
  operator: 'lt' | 'gte' | 'range';
  /** Used by 'lt' and 'gte'. */
  value?: number;
  /** Used by 'range'. From is inclusive. */
  from?: number;
  /** Used by 'range'. To is exclusive. */
  to?: number;
  /** Commission rate in percent for entries that fall in this band. */
  rate: number;
}

export interface DynamicCommissionRule {
  id: string;
  name: string;
  version: number;
  active: boolean;
  /** Required. Scope is always anchored to one bank. */
  bankId: string;
  zoneId?: string;
  /** Recovery department. If set, the entry's department must be this one. */
  departmentId?: string;
  /** Single branch. Kept for compatibility; the Rule Builder uses branchIds. */
  branchId?: string;
  /** Tick-box branch list. If set and not empty, the entry's branch must be one of these. */
  branchIds?: string[];
  userIds?: string[];
  accountIds?: string[];
  categories?: string[];
  productCodes?: string[];
  /** Basis that the amount range is checked against. Required if an amount bound is set. */
  amountBasis?: AmountBasis;
  amountFrom?: number;
  amountTo?: number;
  /** Additional date condition, checked on the collection date. */
  dateFrom?: string;
  dateTo?: string;
  /** Required if any age band is set. */
  ageUnit?: AgeUnit;
  ageBands?: AgeBand[];
  /** General rate in percent. Used when no age band matches (or when there are no bands). */
  generalRate?: number;
  /** Amount the commission is calculated on. */
  commissionBase: AmountBasis;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface RecoveryEntryInput {
  id: string;
  accountId: string;
  bankId: string;
  zoneId?: string;
  departmentId?: string | null;
  branchId?: string;
  userId: string;
  /** YYYY-MM-DD */
  collectionDate: string;
  /** YYYY-MM-DD, from the imported account record. */
  npaDate?: string | null;
  category?: string | null;
  productCode?: string | null;
  /** Each amount basis as stored on the entry or account. Missing = null/undefined. */
  amounts: Partial<Record<AmountBasis, number | null | undefined>>;
}

export type EvaluationStatus = 'READY' | 'BLOCKED' | 'CONFLICT' | 'NO_MATCH';

export interface EvaluationResult {
  status: EvaluationStatus;
  ruleIds: string[];
  rate?: number;
  base?: number;
  commission?: number;
  messages: string[];
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

interface CivilDate {
  y: number;
  m: number; // 1-12
  d: number; // 1-31
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Strict YYYY-MM-DD parse. Returns null for anything that is not a real calendar date. */
export function parseIsoDate(s?: string | null): CivilDate | null {
  if (!s || typeof s !== 'string' || !DATE_RE.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return { y, m, d };
}

function dayNumber(p: CivilDate): number {
  return Date.UTC(p.y, p.m - 1, p.d) / 86400000;
}

/** Last day of a 1-based month. Handles leap years. */
function lastDayOfMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/**
 * Completed age between two dates, in the given unit.
 *   days   : calendar days.
 *   months : whole calendar months completed.
 *   years  : whole years completed (floor of months / 12).
 * Month-end rule: if the NPA day is later than the collection day, the month still
 * counts as complete when the collection falls on the last day of its month
 * (e.g. NPA 31 Jan, collection 28 Feb = 1 month).
 * Requires npa <= coll (checked by the caller).
 */
export function completedAge(npa: CivilDate, coll: CivilDate, unit: AgeUnit): number {
  if (unit === 'days') return dayNumber(coll) - dayNumber(npa);
  let months = (coll.y - npa.y) * 12 + (coll.m - npa.m);
  if (coll.d < npa.d) {
    const isMonthEnd = coll.d === lastDayOfMonth(coll.y, coll.m);
    if (!(isMonthEnd && npa.d > coll.d)) months -= 1;
  }
  return unit === 'months' ? months : Math.floor(months / 12);
}

// ---------------------------------------------------------------------------
// Money (exact integer paise arithmetic)
// ---------------------------------------------------------------------------

const MAX_SAFE_PAISE_PRODUCT = Number.MAX_SAFE_INTEGER;

/**
 * Commission = base x rate / 100, in rupees, rounded half-up to paise.
 * Returns null if the amount is too large to calculate exactly (never a wrong figure).
 */
export function commissionOn(base: number, ratePercent: number): number | null {
  const basePaise = Math.round(base * 100);
  const rateTenThousandths = Math.round(ratePercent * 10000);
  const product = basePaise * rateTenThousandths;
  if (!Number.isSafeInteger(product) || product > MAX_SAFE_PAISE_PRODUCT) return null;
  // paise = basePaise x (rateTenThousandths / 10000) / 100  =  product / 1,000,000
  const paise = Math.floor((product + 500000) / 1000000);
  return paise / 100;
}

// ---------------------------------------------------------------------------
// Validation (run when a rule is saved; the same checks must run on the server)
// ---------------------------------------------------------------------------

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function hasFourDecimalsAtMost(v: number): boolean {
  return Math.abs(Math.round(v * 10000) - v * 10000) < 1e-6;
}

function bandInterval(b: AgeBand): { lo: number; hi: number } | null {
  if (b.operator === 'lt' && isFiniteNumber(b.value)) return { lo: 0, hi: b.value };
  if (b.operator === 'gte' && isFiniteNumber(b.value)) return { lo: b.value, hi: Infinity };
  if (b.operator === 'range' && isFiniteNumber(b.from) && isFiniteNumber(b.to)) return { lo: b.from, hi: b.to };
  return null;
}

/** Returns a list of problems. Empty list = the rule is valid. */
export function validateRule(rule: DynamicCommissionRule): string[] {
  const errors: string[] = [];

  if (!rule.name || !rule.name.trim()) errors.push('Rule name is required.');
  if (!rule.bankId) errors.push('A bank must be selected.');
  if (!AMOUNT_BASES.includes(rule.commissionBase)) errors.push('Commission base is not a supported amount basis.');

  const from = parseIsoDate(rule.effectiveFrom);
  if (!from) errors.push('Effective From date is required and must be a real date (YYYY-MM-DD).');
  if (rule.effectiveTo !== undefined && rule.effectiveTo !== '') {
    const to = parseIsoDate(rule.effectiveTo);
    if (!to) errors.push('Effective To date must be a real date (YYYY-MM-DD).');
    else if (from && dayNumber(to) < dayNumber(from)) errors.push('Effective To cannot be before Effective From.');
  }

  const hasAmountFrom = rule.amountFrom !== undefined;
  const hasAmountTo = rule.amountTo !== undefined;
  if (hasAmountFrom && !(isFiniteNumber(rule.amountFrom) && rule.amountFrom >= 0)) errors.push('Amount From must be zero or more.');
  if (hasAmountTo && !(isFiniteNumber(rule.amountTo) && rule.amountTo >= 0)) errors.push('Amount To must be zero or more.');
  if (hasAmountFrom && hasAmountTo && isFiniteNumber(rule.amountFrom) && isFiniteNumber(rule.amountTo) && rule.amountFrom > rule.amountTo) {
    errors.push('Amount From cannot be greater than Amount To.');
  }
  if ((hasAmountFrom || hasAmountTo) && !rule.amountBasis) errors.push('An amount basis is required when an amount range is set.');
  if (rule.amountBasis && !AMOUNT_BASES.includes(rule.amountBasis)) errors.push('Amount basis is not supported.');

  if (rule.dateFrom || rule.dateTo) {
    const df = parseIsoDate(rule.dateFrom);
    const dt = parseIsoDate(rule.dateTo);
    if (rule.dateFrom && !df) errors.push('Date From must be a real date.');
    if (rule.dateTo && !dt) errors.push('Date To must be a real date.');
    if (df && dt && dayNumber(dt) < dayNumber(df)) errors.push('Date From cannot be after Date To.');
  }

  const bands = rule.ageBands ?? [];
  if (bands.length > 0) {
    if (!rule.ageUnit) errors.push('An age unit (days, months or years) is required when age bands are set.');
    const intervals: { id: string; lo: number; hi: number }[] = [];
    for (const b of bands) {
      if (!isFiniteNumber(b.rate) || b.rate < 0 || b.rate > 100) errors.push(`Age band rate must be between 0 and 100 (band ${b.id}).`);
      if (isFiniteNumber(b.rate) && !hasFourDecimalsAtMost(b.rate)) errors.push(`Rate may have at most 4 decimals (band ${b.id}).`);
      const iv = bandInterval(b);
      if (!iv) {
        errors.push(`Age band has an invalid condition (band ${b.id}).`);
        continue;
      }
      if (iv.lo < 0) errors.push(`Age values cannot be negative (band ${b.id}).`);
      if (b.operator === 'range' && !(iv.lo < iv.hi)) errors.push(`In an age range, From must be less than To (band ${b.id}).`);
      intervals.push({ id: b.id, lo: iv.lo, hi: iv.hi });
    }
    for (let i = 0; i < intervals.length; i++) {
      for (let j = i + 1; j < intervals.length; j++) {
        const a = intervals[i];
        const c = intervals[j];
        if (a.lo < c.hi && c.lo < a.hi) errors.push(`Age bands ${a.id} and ${c.id} overlap.`);
      }
    }
  }

  if (rule.generalRate !== undefined) {
    if (!isFiniteNumber(rule.generalRate) || rule.generalRate < 0 || rule.generalRate > 100) errors.push('General rate must be between 0 and 100.');
    else if (!hasFourDecimalsAtMost(rule.generalRate)) errors.push('Rate may have at most 4 decimals.');
  }
  if (bands.length === 0 && rule.generalRate === undefined) errors.push('A rate is required (general rate or age bands).');

  return errors;
}

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

type RuleOutcome =
  | { kind: 'none' }
  | { kind: 'blocked'; reason: string }
  | { kind: 'conflict'; reason: string }
  | { kind: 'match'; rate: number };

const NONE: RuleOutcome = { kind: 'none' };

function isValidAmount(v: unknown): v is number {
  return isFiniteNumber(v) && v >= 0;
}

function evaluateRule(entry: RecoveryEntryInput, rule: DynamicCommissionRule, coll: CivilDate): RuleOutcome {
  if (!rule.active) return NONE;

  // Scope: a wrong value means the rule does not apply to this entry (NONE).
  // A MISSING value on the entry, where the rule needs one, is BLOCKED: never guessed.
  if (entry.bankId !== rule.bankId) return NONE;
  if (rule.zoneId) {
    if (!entry.zoneId) return { kind: 'blocked', reason: 'zone is missing on the entry' };
    if (entry.zoneId !== rule.zoneId) return NONE;
  }
  if (rule.branchId) {
    if (!entry.branchId) return { kind: 'blocked', reason: 'branch is missing on the entry' };
    if (entry.branchId !== rule.branchId) return NONE;
  }
  if (rule.userIds && rule.userIds.length > 0 && !rule.userIds.includes(entry.userId)) return NONE;
  if (rule.accountIds && rule.accountIds.length > 0) {
    if (!entry.accountId) return { kind: 'blocked', reason: 'account ID is missing on the entry' };
    if (!rule.accountIds.includes(entry.accountId)) return NONE;
  }

  // Effective period, on the collection date, both ends inclusive.
  if (entry.collectionDate < rule.effectiveFrom) return NONE;
  if (rule.effectiveTo && entry.collectionDate > rule.effectiveTo) return NONE;

  // Department and branch list. A missing value on the entry means we cannot
  // tell whether the rule applies, so it is BLOCKED, never treated as a match.
  if (rule.departmentId) {
    if (!entry.departmentId) return { kind: 'blocked', reason: 'recovery department is missing on the entry' };
    if (entry.departmentId !== rule.departmentId) return NONE;
  }
  if (rule.branchIds && rule.branchIds.length > 0) {
    if (!entry.branchId) return { kind: 'blocked', reason: 'branch is missing on the entry' };
    if (!rule.branchIds.includes(entry.branchId)) return NONE;
  }

  // From here the rule is in scope. A missing value means we cannot decide: BLOCKED.
  if (rule.categories && rule.categories.length > 0) {
    if (!entry.category) return { kind: 'blocked', reason: 'account category is missing on the entry' };
    if (!rule.categories.includes(entry.category)) return NONE;
  }
  if (rule.productCodes && rule.productCodes.length > 0) {
    if (!entry.productCode) return { kind: 'blocked', reason: 'product code is missing on the entry' };
    if (!rule.productCodes.includes(entry.productCode)) return NONE;
  }

  if (rule.amountFrom !== undefined || rule.amountTo !== undefined) {
    const basis = rule.amountBasis;
    if (!basis) return { kind: 'blocked', reason: 'amount range has no amount basis' };
    const v = entry.amounts[basis];
    if (!isValidAmount(v)) return { kind: 'blocked', reason: `${basis} amount is missing or invalid (never treated as zero)` };
    if (rule.amountFrom !== undefined && v < rule.amountFrom) return NONE;
    if (rule.amountTo !== undefined && v > rule.amountTo) return NONE;
  }

  if (rule.dateFrom && entry.collectionDate < rule.dateFrom) return NONE;
  if (rule.dateTo && entry.collectionDate > rule.dateTo) return NONE;

  const bands = rule.ageBands ?? [];
  if (bands.length > 0) {
    const npa = parseIsoDate(entry.npaDate);
    if (!npa) return { kind: 'blocked', reason: 'NPA date is missing or invalid, so account age cannot be calculated' };
    if (dayNumber(npa) > dayNumber(coll)) return { kind: 'blocked', reason: 'NPA date is later than the collection date' };
    if (!rule.ageUnit) return { kind: 'blocked', reason: 'age unit is not set on the rule' };

    const age = completedAge(npa, coll, rule.ageUnit);
    const matching = bands.filter((b) => {
      const iv = bandInterval(b);
      return iv !== null && age >= iv.lo && age < iv.hi;
    });
    if (matching.length > 1) {
      return { kind: 'conflict', reason: `more than one age band matches (${matching.map((b) => b.id).join(', ')})` };
    }
    if (matching.length === 1) return { kind: 'match', rate: matching[0].rate };
    if (rule.generalRate !== undefined) return { kind: 'match', rate: rule.generalRate };
    return NONE;
  }

  if (rule.generalRate === undefined) return { kind: 'blocked', reason: 'rule has no rate' };
  return { kind: 'match', rate: rule.generalRate };
}

/**
 * Evaluates one recovery entry against all rules.
 * Precedence: BLOCKED first (never finalize on missing data), then CONFLICT,
 * then READY or NO_MATCH. Never picks a winner by priority or rate.
 */
export function evaluateRecoveryEntry(entry: RecoveryEntryInput, rules: DynamicCommissionRule[]): EvaluationResult {
  if (!entry.bankId || !entry.userId) {
    return { status: 'BLOCKED', ruleIds: [], messages: ['The recovery entry is missing its bank or user.'] };
  }
  const coll = parseIsoDate(entry.collectionDate);
  if (!coll) {
    return { status: 'BLOCKED', ruleIds: [], messages: ['The collection date is missing or invalid.'] };
  }

  const matches: { rule: DynamicCommissionRule; rate: number }[] = [];
  const blockedIds: string[] = [];
  const conflictIds: string[] = [];
  const messages: string[] = [];

  for (const rule of rules) {
    const o = evaluateRule(entry, rule, coll);
    if (o.kind === 'match') matches.push({ rule, rate: o.rate });
    else if (o.kind === 'blocked') {
      blockedIds.push(rule.id);
      messages.push(`Rule ${rule.id} cannot be applied: ${o.reason}.`);
    } else if (o.kind === 'conflict') {
      conflictIds.push(rule.id);
      messages.push(`Rule ${rule.id}: ${o.reason}.`);
    }
  }

  if (blockedIds.length > 0) return { status: 'BLOCKED', ruleIds: blockedIds, messages };
  if (conflictIds.length > 0) return { status: 'CONFLICT', ruleIds: conflictIds, messages };
  if (matches.length === 0) {
    return { status: 'NO_MATCH', ruleIds: [], messages: ['No active rule matches this recovery entry.'] };
  }
  if (matches.length > 1) {
    const ids = matches.map((m) => m.rule.id);
    return {
      status: 'CONFLICT',
      ruleIds: ids,
      messages: [`${ids.length} rules match this entry (${ids.join(', ')}). No rate is chosen automatically; correct the rules.`],
    };
  }

  const { rule, rate } = matches[0];
  const base = entry.amounts[rule.commissionBase];
  if (!isValidAmount(base)) {
    return {
      status: 'BLOCKED',
      ruleIds: [rule.id],
      messages: [`The commission base (${rule.commissionBase}) is missing or invalid for this entry.`],
    };
  }
  const commission = commissionOn(base, rate);
  if (commission === null) {
    return { status: 'BLOCKED', ruleIds: [rule.id], messages: ['The amount is too large to calculate exactly.'] };
  }
  return { status: 'READY', ruleIds: [rule.id], rate, base, commission, messages: [] };
}
